import { useEffect, useState, type RefObject } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ReplayIcon from '@material-ui/icons/Replay';
import type { ReviewResult } from '../../api/types';
import { ReviewPanel } from '../ReviewPanel';
import type { TaskClasses, TaskViewModel } from './TaskListPanel';

type TaskDisplayStatus = TaskViewModel['displayStatus'];

const STATUS_CONFIG: Record<
  TaskDisplayStatus,
  {
    label: string;
    className: string;
  }
> = {
  pending: { label: 'Pendente', className: 'chipPending' },
  waiting: {
    label: 'Aguardando dep.',
    className: 'chipPaused',
  },
  running: { label: 'Rodando', className: 'chipRunning' },
  completed: {
    label: 'Concluída',
    className: 'chipCompleted',
  },
  integrated: {
    label: 'Integrada',
    className: 'chipCompleted',
  },
  error: { label: 'Erro', className: 'chipError' },
  blocked: { label: 'Bloqueada', className: 'chipError' },
  paused: { label: 'Pausada', className: 'chipPaused' },
};

function formatTaskRefs(refs: string[]): string {
  return refs.map(ref => `#${ref}`).join(', ');
}

function getRetrySubtreeLabel(taskNumber: string): string {
  return `Reexecutar task #${taskNumber} e as que dependem dela`;
}

function TaskSummaryRow(props: { classes: TaskClasses; view: TaskViewModel }) {
  const { classes, view } = props;
  const config = STATUS_CONFIG[view.displayStatus];
  return (
    <>
      <Typography className={classes.taskNumber}>
        {view.task.taskNumber}
      </Typography>
      <Typography className={classes.taskTitle}>{view.task.title}</Typography>
      <Chip
        label={config.label}
        size="small"
        className={classes[config.className as keyof typeof classes] as string}
        data-testid={`chip-${view.displayStatus}`}
        aria-label={`Estado da tarefa ${view.task.taskNumber}: ${config.label}`}
      />
      {view.slotInfo && (
        <Chip
          label={`Slot ${view.slotInfo.slot + 1}/${view.slotInfo.totalSlots}`}
          size="small"
          className={classes.slotChip}
          data-testid={`slot-${view.task.taskNumber}`}
          aria-label={`Tarefa ${view.task.taskNumber} no slot ${
            view.slotInfo.slot + 1
          } de ${view.slotInfo.totalSlots}`}
        />
      )}
      {view.staleMinutes !== null && (
        <Chip
          label={`Sem atividade há ${view.staleMinutes} min`}
          size="small"
          className={classes.chipPaused}
          data-testid={`stale-${view.task.taskNumber}`}
        />
      )}
      {view.lastUpdatedLabel && (
        <Typography
          variant="caption"
          className={classes.lastUpdateText}
          data-testid={`last-updated-${view.task.taskNumber}`}
          aria-label={`Última atualização da tarefa ${view.task.taskNumber} às ${view.lastUpdatedLabel}`}
        >
          Atualizada às {view.lastUpdatedLabel}
        </Typography>
      )}
    </>
  );
}

function TaskRelations({ view }: { view: TaskViewModel }) {
  if (view.dependsOnRefs.length === 0 && view.unlocksRefs.length === 0) {
    return null;
  }
  return (
    <Box mb={1}>
      {view.dependsOnRefs.length > 0 && (
        <Typography variant="body2" color="textSecondary">
          Depende de {formatTaskRefs(view.dependsOnRefs)}
        </Typography>
      )}
      {view.unlocksRefs.length > 0 && (
        <Typography variant="body2" color="textSecondary">
          Libera {formatTaskRefs(view.unlocksRefs)}
        </Typography>
      )}
    </Box>
  );
}

function TaskRetryAction(props: {
  classes: TaskClasses;
  taskNumber: string;
  onRetry: (taskNumber: string) => void;
}) {
  const retryLabel = getRetrySubtreeLabel(props.taskNumber);
  const retryDescriptionId = `retry-subtree-${props.taskNumber}`;
  return (
    <>
      <Tooltip title={retryLabel} arrow>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          startIcon={<ReplayIcon />}
          className={props.classes.retryButton}
          onClick={() => props.onRetry(props.taskNumber)}
          aria-label={`Retentar tarefa ${props.taskNumber}`}
          aria-describedby={retryDescriptionId}
        >
          {retryLabel}
        </Button>
      </Tooltip>
      <span id={retryDescriptionId} className={props.classes.srOnly}>
        {retryLabel}
      </span>
    </>
  );
}

function TaskOutput(props: {
  classes: TaskClasses;
  view: TaskViewModel;
  logsContainerRef: RefObject<HTMLDivElement>;
}) {
  if (!props.view.log) return null;
  return (
    <div
      className={props.classes.logsContainer}
      ref={props.view.isActiveStreaming ? props.logsContainerRef : undefined}
    >
      <Typography component="pre" className={props.classes.logsText}>
        {props.view.log}
      </Typography>
    </div>
  );
}

function TaskReview(props: {
  view: TaskViewModel;
  streamingTaskNumber: string | null;
  reviewResult?: ReviewResult;
  isReviewLoading?: boolean;
}) {
  const showReview = props.view.task.taskNumber === props.streamingTaskNumber;
  if (!showReview || (!props.isReviewLoading && !props.reviewResult)) {
    return null;
  }
  return (
    <ReviewPanel
      result={props.reviewResult}
      isLoading={props.isReviewLoading}
    />
  );
}

function TaskErrorMessage(props: {
  classes: TaskClasses;
  view: TaskViewModel;
}) {
  if (!props.view.task.errorMessage) return null;
  return (
    <Typography variant="body2" className={props.classes.errorMessage}>
      Erro: {props.view.task.errorMessage}
    </Typography>
  );
}

function TaskDetails(props: {
  classes: TaskClasses;
  view: TaskViewModel;
  logsContainerRef: RefObject<HTMLDivElement>;
  streamingTaskNumber: string | null;
  reviewResult?: ReviewResult;
  isReviewLoading?: boolean;
  onRetry: (taskNumber: string) => void;
}) {
  const { classes, view } = props;
  return (
    <Box width="100%">
      <Typography variant="body2">{view.stateDescription}</Typography>
      <TaskRelations view={view} />
      {view.staleMinutes !== null && (
        <Typography variant="body2" color="textSecondary" role="alert">
          Possivelmente travada: sem atividade há {view.staleMinutes} min.
        </Typography>
      )}
      <TaskOutput
        classes={classes}
        view={view}
        logsContainerRef={props.logsContainerRef}
      />
      <TaskReview
        view={view}
        streamingTaskNumber={props.streamingTaskNumber}
        reviewResult={props.reviewResult}
        isReviewLoading={props.isReviewLoading}
      />
      <TaskErrorMessage classes={classes} view={view} />
      {view.displayStatus === 'error' && (
        <TaskRetryAction
          classes={classes}
          taskNumber={view.task.taskNumber}
          onRetry={props.onRetry}
        />
      )}
      {!view.log &&
        !view.task.errorMessage &&
        view.displayStatus === 'pending' && (
          <Typography variant="body2" color="textSecondary">
            Aguardando execução...
          </Typography>
        )}
    </Box>
  );
}

export function TaskAccordionItem(props: {
  classes: TaskClasses;
  view: TaskViewModel;
  logsContainerRef: RefObject<HTMLDivElement>;
  streamingTaskNumber: string | null;
  reviewResult?: ReviewResult;
  isReviewLoading?: boolean;
  onRetry: (taskNumber: string) => void;
}) {
  const shouldAutoExpand =
    props.view.displayStatus === 'running' ||
    props.view.displayStatus === 'error' ||
    props.view.displayStatus === 'waiting';
  const [expanded, setExpanded] = useState(shouldAutoExpand);

  useEffect(() => {
    if (shouldAutoExpand) setExpanded(true);
  }, [shouldAutoExpand]);

  return (
    <Accordion
      key={props.view.task.id}
      className={props.classes.accordion}
      expanded={expanded}
      onChange={(_event, nextExpanded) => setExpanded(nextExpanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={props.classes.accordionSummary}
      >
        <TaskSummaryRow classes={props.classes} view={props.view} />
      </AccordionSummary>
      <AccordionDetails>
        <TaskDetails {...props} />
      </AccordionDetails>
    </Accordion>
  );
}
